import { TableCellTypes } from '@carboledger/ui';
import {
  makeLinkCell,
  makeUnitTypeCell,
  makeUnitScopedCell,
  makeDiscrepanciesCell,
} from '@/components/common';
import { OUTGOING_SHIPMENT_STATUS_TONE } from '@/config/statusTones';
import {
  OUTGOING_SHIPMENT_COLUMNS,
  OUTGOING_SHIPMENT_SORT_FIELDS,
} from './outgoingShipments.constants';
import { buildStatusVariants } from './outgoingShipmentStatus';
import { buildRowUnitOptions, unitTypeLabel } from './outgoingShipmentUnits';

// One entry per column, in design order; `sortable` is derived from OUTGOING_SHIPMENT_SORT_FIELDS below.
const canSort = (id) => Boolean(OUTGOING_SHIPMENT_SORT_FIELDS[id]);

const renderUnitTypeCell = makeUnitTypeCell((t, row) => buildRowUnitOptions(t, row.byUnit));
const renderShipmentQuantity = makeUnitScopedCell('shipmentQuantity');
const renderAllocatedQuantity = makeUnitScopedCell('allocatedQuantity');
const renderRemainingToAllocate = makeUnitScopedCell('remainingToAllocateQuantity');

export const buildOutgoingShipmentsColumns = (
  t,
  { unitHeaderSelect, onOpenShipment, onOpenDiscrepancy },
) =>
  [
    {
      id: OUTGOING_SHIPMENT_COLUMNS.shipmentId,
      header: t('FIELD_SHIPMENT_ID'),
      type: TableCellTypes.custom,
      width: 140,
      pinnable: true,
      cell: makeLinkCell(onOpenShipment),
    },
    {
      id: OUTGOING_SHIPMENT_COLUMNS.materialName,
      header: t('FIELD_MATERIAL_NAME'),
      type: TableCellTypes.text,
      width: 200,
    },
    {
      id: OUTGOING_SHIPMENT_COLUMNS.linkedOrders,
      header: t('OUTGOING_SHIPMENTS_COL_LINKED_ORDERS'),
      type: TableCellTypes.text,
      width: 160,
    },
    {
      id: OUTGOING_SHIPMENT_COLUMNS.facilityName,
      header: t('FIELD_FACILITY_NAME'),
      type: TableCellTypes.text,
      width: 143,
    },
    {
      id: OUTGOING_SHIPMENT_COLUMNS.status,
      header: t('FIELD_STATUS'),
      type: TableCellTypes.statusDot,
      width: 160,
      variantMap: buildStatusVariants(t, OUTGOING_SHIPMENT_STATUS_TONE),
    },
    {
      id: OUTGOING_SHIPMENT_COLUMNS.soldTo,
      header: t('FIELD_SOLD_TO'),
      type: TableCellTypes.text,
      width: 192,
    },
    {
      id: OUTGOING_SHIPMENT_COLUMNS.terminal,
      header: t('FIELD_TERMINAL'),
      type: TableCellTypes.text,
      width: 141,
    },
    {
      id: OUTGOING_SHIPMENT_COLUMNS.vesselName,
      header: t('FIELD_VESSEL_NAME'),
      type: TableCellTypes.text,
      width: 141,
    },
    {
      id: OUTGOING_SHIPMENT_COLUMNS.shippingTo,
      header: t('FIELD_SHIPPING_TO'),
      type: TableCellTypes.text,
      width: 192,
    },
    {
      id: OUTGOING_SHIPMENT_COLUMNS.unitType,
      header: t('FIELD_UNIT_TYPE', {
        unit: unitTypeLabel(t, unitHeaderSelect.value),
      }),
      type: TableCellTypes.custom,
      width: 252,
      cell: renderUnitTypeCell,
      headerSelect: unitHeaderSelect,
    },
    {
      id: OUTGOING_SHIPMENT_COLUMNS.shipmentQuantity,
      header: t('FIELD_SHIPMENT_QUANTITY'),
      type: TableCellTypes.custom,
      width: 200,
      cell: renderShipmentQuantity,
    },
    {
      id: OUTGOING_SHIPMENT_COLUMNS.allocatedQuantity,
      header: t('FIELD_ALLOCATED_QUANTITY'),
      type: TableCellTypes.custom,
      width: 200,
      cell: renderAllocatedQuantity,
    },
    {
      id: OUTGOING_SHIPMENT_COLUMNS.remainingToAllocateQuantity,
      header: t('OUTGOING_SHIPMENTS_COL_REMAINING_TO_ALLOCATE'),
      type: TableCellTypes.custom,
      width: 200,
      cell: renderRemainingToAllocate,
    },
    {
      id: OUTGOING_SHIPMENT_COLUMNS.dateOfDispatch,
      header: t('FIELD_DATE_OF_DISPATCH'),
      type: TableCellTypes.text,
      width: 150,
    },
    {
      id: OUTGOING_SHIPMENT_COLUMNS.discrepancies,
      header: t('FIELD_DISCREPANCIES'),
      type: TableCellTypes.custom,
      width: 200,
      cell: makeDiscrepanciesCell(onOpenDiscrepancy),
    },
  ].map((column) => ({ ...column, sortable: canSort(column.id) }));
